import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useRecordDuplicates } from '@/api/leads/use-leads';
import DuplicateGroupCard, { DuplicateMergeResult } from './DuplicateGroupCard';

interface DuplicateBadgeProps {
    /** The lead or customer shown on the page. */
    recordId: string;
}

/**
 * "Duplicate" badge for a lead or customer page. Shown only when the record shares an
 * email or phone with another lead/customer; opens the group so it can be merged.
 */
const DuplicateBadge: React.FC<DuplicateBadgeProps> = ({ recordId }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const { data: group } = useRecordDuplicates(recordId);

    if (!group) return null;

    const others = group.records.length - 1;

    const handleMerged = ({ survivor, mergedIds }: DuplicateMergeResult) => {
        setOpen(false);
        // This page's record was merged away: continue on the record it was merged into.
        if (mergedIds.includes(recordId)) {
            navigate(
                survivor.type === 'customer' ? `/customers/${survivor.id}` : `/leads/${survivor.id}`
            );
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex h-7 items-center gap-1.5 rounded-full border border-warning/40 bg-warning/10 px-2.5 text-xs font-medium text-warning transition-colors hover:bg-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Duplicate: shares an email or phone with ${others} other record${others !== 1 ? 's' : ''}`}
                title={`Shares an email or phone with ${others} other record${others !== 1 ? 's' : ''}`}
            >
                <Copy className="h-3.5 w-3.5" />
                Duplicate
                {others > 1 && <span className="tabular-nums">· {others}</span>}
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
                    <DialogHeader>
                        <DialogTitle>Possible duplicates</DialogTitle>
                        <DialogDescription>
                            These records share an email or a phone number. Pick the record to keep:
                            it keeps its own values and only its empty fields are filled from the
                            merged leads, whose notes, tasks and history move to it before they are
                            deleted. A lead is always merged into a customer.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
                        <DuplicateGroupCard
                            group={group}
                            currentId={recordId}
                            onMerged={handleMerged}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DuplicateBadge;
